import React, { useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Modal,
  ModalFooter,
  Button,
  TextField,
  Row,
  Col,
  Label,
} from '@folio/stripes/components';
import { FormattedMessage, useIntl } from 'react-intl';
import { Form, Field, useField } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import createDecorator from 'final-form-focus';
import { v4 as uuidv4 } from 'uuid';
import { LocationSelectionWithCheck } from '@folio/inventory/src/edit/common/LocationSelectionWithCheck';
import { setDefaultValues } from '../../../../../../../utils/utils';
import useLocationName from '../../../../../../../hooks/useLocationName';

// Create focus decorator for automatic focus on validation errors
const focusOnError = createDecorator();


const propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  template: PropTypes.object.isRequired,
  controlledVocab: PropTypes.object.isRequired,
  currentHoldingsCount: PropTypes.number.isRequired,
  isProcessing: PropTypes.bool,
  instanceStatisticalCodeIds: PropTypes.array,
};

function BatchCreateHoldingsModal({
  isOpen,
  onClose,
  onConfirm,
  template,
  controlledVocab,
  currentHoldingsCount,
  isProcessing = false,
  instanceStatisticalCodeIds = [],
}) {
  const [displayHoldingCount, setDisplayHoldingCount] = useState(1);
  const [selectedLocationId, setSelectedLocationId] = useState('');
  const intl = useIntl();

  // Get location name using useLocationName hook
  const { locationName } = useLocationName(selectedLocationId, controlledVocab);

  // Get classification data from instance for call number generation
  const classificationField = useField('instanceData.classifications');

  // Create initial holding data based on template and classification
  const createInitialHoldingData = useCallback(() => {
    // Get template defaults for holdings
    const templateDefaults = template?.templateFields?.holdings
      ? setDefaultValues(template.templateFields.holdings)
      : {};

    // Extract classification data for call number generation
    const classification = classificationField?.input?.value?.[0];
    const classificationNumber = classification?.classificationNumber || '';
    const authorNumber = classification?.authorNumber
      ? ` ${classification.authorNumber}`
      : '';

    // Generate call number from classification
    const callNumber =
      classificationNumber && authorNumber
        ? `${classificationNumber}${authorNumber}`
        : classificationNumber || '';

    // Combine template defaults with auto-generated call number
    return {
      id: '',
      ...templateDefaults,
      // Override with auto-generated call number if available
      ...(callNumber && { callNumber }),
      // Propagate statistical codes from instance
      ...(instanceStatisticalCodeIds.length && { holdingStatisticalCodeIds: instanceStatisticalCodeIds }),
      // Location will be set from form input
      permanentLocationId: '',
    };
  }, [template, classificationField?.input?.value]);

  const handleSubmit = useCallback(
    (values) => {
      const sharedLocationId = values.permanentLocationId;
      const holdingsToCreate = [];

      // Create holdings with shared settings
      for (let i = 0; i < displayHoldingCount; i++) {
        const holdingId = uuidv4();
        const newHolding = {
          ...createInitialHoldingData(),
          id: holdingId,
          holdingHrid: '',
          holdingCopyNumber: currentHoldingsCount + i + 1,
          permanentLocationId: sharedLocationId,
        };
        holdingsToCreate.push(newHolding);
      }

      onConfirm(holdingsToCreate);
      onClose();
      return undefined;
    },
    [
      displayHoldingCount,
      createInitialHoldingData,
      currentHoldingsCount,
      onConfirm,
      onClose,
    ],
  );


  const validate = useCallback(
    (values) => {
      const errors = {};

      // Validate holding count from form values instead of state
      const formHoldingCount = parseInt(values.holdingCount, 10);
      if (!formHoldingCount || formHoldingCount < 1) {
        errors.holdingCount = intl.formatMessage({
          id: 'ui-plugin-inventory-templates.batchCreateHoldingsModal.validation.mustBeAtLeastOne',
          defaultMessage: 'Must be at least 1',
        });
      }

      if (!values.permanentLocationId) {
        errors.permanentLocationId = intl.formatMessage({
          id: 'ui-plugin-inventory-templates.batchCreateHoldingsModal.validation.required',
          defaultMessage: 'Required',
        });
      }

      return errors;
    },
    [intl],
  );

  const initialValues = useMemo(() => ({
    holdingCount: '1',
    permanentLocationId: '',
  }), []);

  // Custom required validation function with translation
  const requiredValidation = (value) => {
    if (!value) {
      return intl.formatMessage({
        id: 'ui-plugin-inventory-templates.batchCreateHoldingsModal.validation.required',
        defaultMessage: 'Required',
      });
    }
    return undefined;
  };


  return (
    <Form
      onSubmit={handleSubmit}
      mutators={{ ...arrayMutators }}
      decorators={[focusOnError]}
      initialValues={initialValues}
      validate={validate}
      render={({
        handleSubmit: formSubmit,
        submitting,
        values,
        hasValidationErrors,
      }) => {
        // Use the display count for all preview displays
        const currentFormHoldingCount = displayHoldingCount;

        // Update selectedLocationId when permanentLocationId changes
        if (selectedLocationId !== values.permanentLocationId) {
          setSelectedLocationId(values.permanentLocationId || '');
        }

        // Get translated location name
        const selectedLocationName = locationName || 'Unknown Location';

        // Create footer JSX without memoization to avoid hooks rule violation
        const renderFooter = (
          <ModalFooter>
            <Button
              buttonStyle="primary"
              type="submit"
              onClick={(e) => {
                e.preventDefault();
                formSubmit();
              }}
              disabled={submitting || hasValidationErrors || isProcessing}
              marginBottom0
            >
              {isProcessing ? (
                <FormattedMessage
                  id="ui-plugin-inventory-templates.batchCreateHoldingsModal.creating"
                  defaultMessage="Creating Holdings..."
                />
              ) : (
                <FormattedMessage
                  id="ui-plugin-inventory-templates.batchCreateHoldingsModal.create"
                  values={{ count: currentFormHoldingCount }}
                  defaultMessage="Create {count} Holdings"
                />
              )}
            </Button>
            <Button
              buttonStyle="default"
              onClick={onClose}
              disabled={isProcessing}
              marginBottom0
            >
              <FormattedMessage
                id="ui-plugin-inventory-templates.batchCreateHoldingsModal.cancel"
                defaultMessage="Cancel"
              />
            </Button>
          </ModalFooter>
        );

        return (
          <Modal
            open={isOpen}
            onClose={onClose}
            footer={renderFooter}
            wrappingElement="form"
            label={
              <FormattedMessage
                id="ui-plugin-inventory-templates.batchCreateHoldingsModal.title"
                defaultMessage="Create Multiple Holdings"
              />
            }
            size="medium"
          >
            <form onSubmit={formSubmit}>
              <div style={{ padding: '16px' }}>
                <div
                  style={{
                    marginBottom: '16px',
                    padding: '12px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '4px',
                    fontSize: '14px',
                    color: '#6c757d',
                  }}
                >
                  <FormattedMessage
                    id="ui-plugin-inventory-templates.batchCreateHoldingsModal.description"
                    defaultMessage="Create multiple holdings with shared settings. All holdings will inherit call numbers from classification data and other settings from the template."
                  />
                </div>

                {/* Holdings count input */}
                <Row>
                  <Col xs={12} md={6}>
                    <TextField
                      label={
                        <FormattedMessage
                          id="ui-plugin-inventory-templates.batchCreateHoldingsModal.count.label"
                          defaultMessage="Number of Holdings to Create"
                        />
                      }
                      placeholder={intl.formatMessage({
                        id: 'ui-plugin-inventory-templates.batchCreateHoldingsModal.count.placeholder',
                        defaultMessage: 'Enter number of holdings to create'
                      })}
                      type="number"
                      min="1"
                      value={displayHoldingCount}
                      onChange={(e) => {
                        const count = parseInt(e.target.value, 10);
                        if (!Number.isNaN(count) && count >= 1) {
                          setDisplayHoldingCount(count);
                        }
                      }}
                      required
                    />
                  </Col>
                </Row>

                {/* Shared location selection */}
                <Row>
                  <Col xs={12}>
                    <Field
                      name="permanentLocationId"
                      component={LocationSelectionWithCheck}
                      fullWidth
                      label={
                        <Label fullWidth>
                          <FormattedMessage
                            id="ui-plugin-inventory-templates.batchCreateHoldingsModal.location.label"
                            defaultMessage="Shared Location (All holdings will use this location)"
                          />
                        </Label>
                      }
                      validate={requiredValidation}
                      required
                    />
                  </Col>
                </Row>

                {/* Preview section */}
                {displayHoldingCount > 0 && values.permanentLocationId && (
                  <div
                    style={{
                      marginTop: '24px',
                      padding: '16px',
                      backgroundColor: '#f0f9ff',
                      border: '1px solid #bfdbfe',
                      borderRadius: '6px',
                    }}
                  >
                    <div
                      style={{
                        fontWeight: 'bold',
                        marginBottom: '8px',
                        color: '#1976d2',
                      }}
                    >
                      <FormattedMessage
                        id="ui-plugin-inventory-templates.batchCreateHoldingsModal.preview.title"
                        defaultMessage="Preview:"
                      />
                    </div>
                    <ul
                      style={{
                        margin: 0,
                        paddingLeft: '20px',
                        color: '#0c4a6e',
                      }}
                    >
                      <li style={{ marginBottom: '4px' }}>
                        <FormattedMessage
                          id="ui-plugin-inventory-templates.batchCreateHoldingsModal.preview.count"
                          values={{ count: currentFormHoldingCount }}
                          defaultMessage="Creating {count} holdings"
                        />
                      </li>
                      <li style={{ marginBottom: '4px' }}>
                        <FormattedMessage
                          id="ui-plugin-inventory-templates.batchCreateHoldingsModal.preview.location"
                          values={{ location: <strong>{selectedLocationName}</strong> }}
                          defaultMessage="All in location: {location}"
                        />
                      </li>
                      <li style={{ marginBottom: '4px' }}>
                        <FormattedMessage
                          id="ui-plugin-inventory-templates.batchCreateHoldingsModal.preview.copyNumbers"
                          values={{
                            start: currentHoldingsCount + 1,
                            end: currentHoldingsCount + currentFormHoldingCount,
                          }}
                          defaultMessage="Copy numbers: {start} to {end}"
                        />
                      </li>
                      <li style={{ marginBottom: '4px' }}>
                        <FormattedMessage
                          id="ui-plugin-inventory-templates.batchCreateHoldingsModal.preview.callNumbers"
                          defaultMessage="Call numbers will be generated from classification data"
                        />
                      </li>
                      <li>
                        <FormattedMessage
                          id="ui-plugin-inventory-templates.batchCreateHoldingsModal.preview.template"
                          defaultMessage="Other settings will inherit from template defaults"
                        />
                      </li>
                    </ul>
                  </div>
                )}
              </div>
            </form>
          </Modal>
        );
      }}
    />
  );
}

BatchCreateHoldingsModal.propTypes = propTypes;

export default BatchCreateHoldingsModal;
